import React, { useState, useEffect } from 'react';
import { sanitizeTextInput, sanitizeTextInputRealTime } from '../../utils/sanitization';
import useModal from '../../hooks/useModal';

/**
 * Modal genérico para crear/editar entidades con formularios dinámicos.
 * Renderiza campos según configuración recibida, sanitiza inputs de texto,
 * y soporta uploads de archivos via FormData.
 *
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.isOpen - Controla si el modal está visible
 * @param {Function} props.onClose - Callback para cerrar el modal
 * @param {Function} props.onSave - Callback al guardar, recibe Object o FormData si hay archivos
 * @param {string} [props.title='Formulario'] - Título mostrado en el header
 * @param {Array} [props.fields=[]] - Configuración de campos del formulario
 * @param {Object|null} [props.initialData=null] - Datos para edición (null = modo crear)
 * @param {boolean} [props.loading=false] - Si true, deshabilita form y muestra "Guardando..."
 * @param {string} [props.submitLabel] - Texto custom para botón submit (default: 'Crear'/'Actualizar')
 * @param {Function} [props.crossFieldValidator] - Validación cross-field opcional
 * @param {string} [props.confirmationMessage] - Si se proporciona, muestra un checkbox de confirmación
 *   que debe marcarse antes de poder enviar el formulario (ej: "Esta acción no se puede deshacer")
 * @returns {JSX.Element|null} Null si isOpen es false
 *
 * @example
 * const fields = [
 *   {
 *     name: 'email',
 *     label: 'Email',
 *     type: 'text',
 *     required: true,
 *     validators: [validateEmail]
 *   },
 *   {
 *     name: 'fecha',
 *     label: 'Fecha',
 *     type: 'date',
 *     required: true,
 *     validators: [(value) => validateDate(value, 'Fecha')]
 *   }
 * ];
 *
 * <EntityModal
 *   fields={fields}
 *   crossFieldValidator={(formData) => {
 *     const errors = {};
 *     const result = validateDateRange(formData.inicio, formData.fin);
 *     if (!result.valid) errors.fin = result.message;
 *     return errors;
 *   }}
 * />
 */
function EntityModal({
  isOpen,
  onClose,
  onSave,
  title = 'Formulario',
  fields = [],
  initialData = null,
  loading = false,
  submitLabel,
  crossFieldValidator,
  confirmationMessage
}) {
  const [formData, setFormData] = useState({});
  const [fileNames, setFileNames] = useState({});
  const [errors, setErrors] = useState({});
  const [confirmed, setConfirmed] = useState(false);

  // Inicializar formData desde fields e initialData
  useEffect(() => {
    const initial = {};
    const initialFileNames = {};
    fields.forEach(field => {
      if (field.type === 'file') {
        initial[field.name] = null;
        if (initialData?.[field.fileNameField]) {
          initialFileNames[field.name] = initialData[field.fileNameField];
        }
      } else if (field.type === 'checkbox') {
        // Usar Number() para manejar correctamente: 0, 1, "0", "1", true, false
        // Boolean("0") = true (incorrecto), Number("0") === 1 = false (correcto)
        initial[field.name] = Number(initialData?.[field.name] ?? 0) === 1;
      } else if (field.type === 'date') {
        // Normalizar ISO datetime (2026-03-15T00:00:00.000Z) a YYYY-MM-DD para input type="date"
        const raw = initialData?.[field.name] ?? field.defaultValue ?? '';
        initial[field.name] = typeof raw === 'string' && raw.includes('T') ? raw.split('T')[0] : raw;
      } else {
        initial[field.name] = initialData?.[field.name] ?? field.defaultValue ?? '';
      }
    });
    setFormData(initial);
    setFileNames(initialFileNames);
    setErrors({});
    setConfirmed(false);
  }, [fields, initialData, isOpen]);

  /**
   * Permite a callbacks de campo actualizar otros campos del formulario
   * @param {string} name - Nombre del campo a actualizar
   * @param {*} value - Valor a asignar
   */
  const setFieldValue = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  /**
   * Maneja cambios en inputs de texto, select, date, number.
   * Si el campo define `onChange`, lo invoca con (value, setFieldValue, formData).
   * @param {Event} e - Evento del input
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const field = fields.find(f => f.name === name);

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
      if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
      if (field?.onChange) {
        field.onChange(checked, setFieldValue, { ...formData, [name]: checked });
      }
      return;
    }

    // For text and textarea: use real-time sanitization (allows spaces)
    // For other types: no sanitization needed
    const sanitized = field?.type === 'text' || field?.type === 'textarea'
      ? sanitizeTextInputRealTime(value)
      : field?.type === 'select' || field?.type === 'number' || field?.type === 'date'
      ? value
      : value;

    setFormData(prev => ({ ...prev, [name]: sanitized }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (field?.onChange) {
      field.onChange(sanitized, setFieldValue, { ...formData, [name]: sanitized });
    }
  };

  /**
   * Maneja cambios en inputs de tipo file
   * @param {Event} e - Evento del input file
   */
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0] || null;
    setFormData(prev => ({ ...prev, [name]: file }));
    setFileNames(prev => ({ ...prev, [name]: file?.name || '' }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  /**
   * Valida campos requeridos y ejecuta validadores custom
   * @param {Object} [dataToValidate=formData] - Datos a validar
   * @returns {Object} Objeto con errores por campo
   */
  const validate = (dataToValidate = formData) => {
    const newErrors = {};

    // 1. Validar required (existente)
    fields.forEach(field => {
      // Evaluar si el campo está oculto
      const isHidden = field.hidden &&
        (typeof field.hidden === 'function'
          ? field.hidden(dataToValidate)
          : field.hidden);

      // No validar campos ocultos
      if (isHidden) return;

      if (field.required) {
        if (field.type === 'file') {
          const hasExistingFile = initialData?.[field.fileNameField];
          const hasNewFile = dataToValidate[field.name];
          if (!hasExistingFile && !hasNewFile) {
            newErrors[field.name] = `${field.label} es requerido`;
          }
        } else if (!dataToValidate[field.name]) {
          newErrors[field.name] = `${field.label} es requerido`;
        }
      }

      // 2. Validar con validators (nuevo)
      if (field.validators && Array.isArray(field.validators)) {
        const value = dataToValidate[field.name];
        // Solo validar si tiene valor (required ya validó vacío)
        if (value) {
          for (const validator of field.validators) {
            const result = validator(value);
            if (result && !result.valid) {
              newErrors[field.name] = result.message;
              break; // Detener en primer error
            }
          }
        }
      }
    });

    // 3. Validación cross-field (nuevo)
    if (crossFieldValidator && typeof crossFieldValidator === 'function') {
      const crossFieldErrors = crossFieldValidator(dataToValidate);
      Object.assign(newErrors, crossFieldErrors);
    }

    return newErrors;
  };

  /**
   * Maneja el envío del formulario
   * @param {Event} e - Evento submit
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    // Apply final sanitization with trim before validation
    const finalData = {};
    fields.forEach(field => {
      const value = formData[field.name];
      if (field.type === 'file') {
        finalData[field.name] = value;
      } else {
        finalData[field.name] = field.type === 'checkbox'
          ? (formData[field.name] ? 1 : 0)
          : (field.type === 'text' || field.type === 'textarea'
             || (field.type === 'custom' && typeof value === 'string'))
          ? sanitizeTextInput(value)
          : value;
      }
    });

    // Validate using final data
    const validationErrors = validate(finalData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Detectar si hay campos tipo file
    const hasFileFields = fields.some(f => f.type === 'file');

    if (hasFileFields) {
      const formDataObj = new FormData();
      Object.entries(finalData).forEach(([key, value]) => {
        if (value !== null && value !== '') {
          formDataObj.append(key, value);
        }
      });
      onSave(formDataObj);
    } else {
      onSave(finalData);
    }
  };

  /**
   * Cierra el modal y limpia el estado
   */
  const handleClose = () => {
    if (!loading) {
      setFormData({});
      setErrors({});
      onClose();
    }
  };

  const { handleBackdropClick } = useModal(isOpen, handleClose, { loading });

  /**
   * Renderiza un campo según su tipo
   * @param {Object} field - Configuración del campo
   * @returns {JSX.Element}
   */
  const renderField = (field) => {
    const { name, label, type, required, options, placeholder, maxLength } = field;
    const value = formData[name] ?? '';
    const error = errors[name];

    const labelElement = (
      <label htmlFor={name} className="form-label">
        {label} {required && <span className="text-danger">*</span>}
      </label>
    );

    const errorElement = error && (
      <div className="invalid-feedback">{error}</div>
    );

    switch (type) {
      case 'select':
        return (
          <div className="form-group" key={name}>
            {labelElement}
            <select
              id={name}
              name={name}
              className={`form-select ${error ? 'error' : ''}`}
              value={value}
              onChange={handleChange}
              disabled={loading || field.disabled}
            >
              <option value="">Seleccione...</option>
              {options?.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {errorElement}
          </div>
        );

      case 'textarea':
        return (
          <div className="form-group" key={name}>
            {labelElement}
            <textarea
              id={name}
              name={name}
              className={`form-textarea ${error ? 'error' : ''}`}
              value={value}
              onChange={handleChange}
              placeholder={placeholder}
              maxLength={maxLength}
              disabled={loading}
              rows={3}
            />
            {errorElement}
          </div>
        );

      case 'date':
        return (
          <div className="form-group" key={name}>
            {labelElement}
            <input
              type="date"
              id={name}
              name={name}
              className={`form-control ${error ? 'error' : ''}`}
              value={value}
              onChange={handleChange}
              min={field.min}
              max={field.max}
              disabled={loading}
            />
            {errorElement}
          </div>
        );

      case 'number':
        return (
          <div className="form-group" key={name}>
            {labelElement}
            <input
              type="number"
              id={name}
              name={name}
              className={`form-control ${error ? 'error' : ''}`}
              value={value}
              onChange={handleChange}
              placeholder={placeholder}
              min={field.min}
              max={field.max}
              step={field.step}
              disabled={loading}
            />
            {errorElement}
          </div>
        );

      case 'file':
        return (
          <div className="form-group" key={name}>
            {labelElement}
            <input
              type="file"
              id={name}
              name={name}
              className={`form-control ${error ? 'error' : ''}`}
              accept={field.accept || '*'}
              onChange={handleFileChange}
              disabled={loading}
            />
            {field.hint && <small className="text-muted">{field.hint}</small>}
            {fileNames[name] && (
              <div className="margin-top-sm">
                <span className="badge badge-info">
                  <i className="fas fa-file"></i> {fileNames[name]}
                </span>
              </div>
            )}
            {errorElement}
          </div>
        );

      case 'checkbox':
        return (
          <div className="form-check form-group" key={name}>
            <input
              type="checkbox"
              className="form-check-input"
              id={name}
              name={name}
              checked={Boolean(value)}
              onChange={handleChange}
              disabled={loading}
            />
            <label className="form-check-label" htmlFor={name}>
              {label} {required && <span className="text-danger">*</span>}
            </label>
            {errorElement}
          </div>
        );

      case 'custom':
        return (
          <div className="form-group" key={name}>
            {labelElement}
            {field.renderField(
              value,
              (newValue) => handleChange({ target: { name, value: newValue } }),
              { formData, setFieldValue, error, disabled: loading }
            )}
            {errorElement}
          </div>
        );

      default: // text, password, email, etc.
        return (
          <div className="form-group" key={name}>
            {labelElement}
            <input
              type={field.type || 'text'}
              id={name}
              name={name}
              className={`form-control ${error ? 'error' : ''}`}
              value={value}
              onChange={handleChange}
              placeholder={placeholder}
              maxLength={maxLength || 100}
              disabled={loading}
            />
            {errorElement}
          </div>
        );
    }
  };

  if (!isOpen) return null;

  // Filtrar campos según propiedad hidden
  const visibleFields = fields.filter(field => {
    if (field.hidden === undefined) return true;
    if (typeof field.hidden === 'function') {
      return !field.hidden(formData);
    }
    return !field.hidden;
  });

  const hasHalfFields = visibleFields.some(f => f.width === 'half');

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className={`modal-container${hasHalfFields ? ' modal-container-wide' : ''}`}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button
            className="modal-close-btn"
            onClick={handleClose}
            disabled={loading}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={`modal-body${hasHalfFields ? ' form-row' : ''}`}>
            {hasHalfFields
              ? visibleFields.map(field => (
                  <div key={field.name} className={field.width === 'half' ? 'col-md-6' : 'col-md-12'}>
                    {renderField(field)}
                  </div>
                ))
              : visibleFields.map(renderField)
            }
          </div>

          {confirmationMessage && (
            <div className="modal-confirmation">
              <label className="form-check-inline">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  disabled={loading}
                />
                <span className="text-warning-subtle">{confirmationMessage}</span>
              </label>
            </div>
          )}

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || (confirmationMessage && !confirmed)}
            >
              {loading ? 'Procesando...' : (submitLabel || (initialData ? 'Actualizar' : 'Crear'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EntityModal;
